import { store } from "@/store"
import {
  deleteCart,
  removeFromCartLineItem,
  setCart,
  setCartCount,
  setShipping,
  shipmentCart,
  shipmentUpdate,
  createCart,
  recentlyViewed,
  setNoshipping,
} from "@/store/features/cartSlice"
import { getProfile } from "@/store/features/checkoutSlice"
import { apim } from "@/constants/api"
import { getShippingDiscountPromoCodes, arrayMove } from "@/utils/helper"

import { removeFromCart, setRemoveCartPayload } from "@/utils/removeCart"
import { getQuantityCount } from "@/js/helperV2"
import { shippingCostEndPoint } from "@/constants/index"
import { showToast } from "@/store/features/genericSlice"

const setShippingAddress = async (data, cb, needAnalytics, errorCb) => {
  const { user = {} } = store.getState()?.auth
  if (data.postalCode && data.postalCode.trim() !== "") {
    const address = {
      postalCode: data.postalCode?.trim(),
      state: data.state?.trim(),
      country: data.country?.trim(),
    }
    const {
      firstName,
      lastName,
      streetName,
      streetNumber,
      city,
      email,
      phone,
    } = data
    if (firstName) address.firstName = firstName.trim()
    if (lastName) address.lastName = lastName.trim()
    if (streetName) address.streetName = streetName.trim()
    if (streetNumber) address.streetNumber = streetNumber.trim()
    if (city) address.city = city.trim()
    if (email) address.email = email.trim().toLowerCase()
    if (phone) address.phone = phone.trim()
    const actions = [
      {
        action: "setShippingAddress",
        address: address,
      },
    ]
    if (email)
      actions.push({
        action: "setCustomerEmail",
        email: email.trim().toLowerCase(),
      })
    if (user?.koc) {
      actions.push({
        action: "setCustomType",
        type: { key: "order", typeId: "type" },
        fields: { CSRAssociateId: user?.koc, orderType: "ISC" },
      })
    }
    const payload = {
      actions: actions,
    }
    await store
      .dispatch(shipmentUpdate(payload))
      .unwrap()
      .then(res => {
        if (res?.type === "Cart" && res.lineItems.length > 0) {
          store.dispatch(setCart(res))
          const quantityLineItems = res?.lineItems
          const payload = getQuantityCount(quantityLineItems)
          store.dispatch(setCartCount(payload))
          getShippingMethods()
          cb && cb(address.postalCode, "success")
          needAnalytics && needAnalytics("success")
        } else {
          if (!phone) {
            cb && cb(address.postalCode, "failure", "no items to ship in cart")
          }
        }
      })
      .catch(error => {
        errorCb && errorCb(error)
        if (!phone) {
          cb &&
            cb(address.postalCode, "failure", "failed to set shipping address.")
        }
      })
  }
}

const getShippingMethods = async () => {
  const cart = store?.getState()?.cart?.cart
  const lineItemsLength = cart?.lineItems ? cart.lineItems.length : 0
  // const taxedPrice = getState().cart?.cart?.data?.taxedPrice;
  const shippingZip = localStorage.getItem("shippingZip")
  const {
    taxedPrice = "",
    shippingAddress: { postalCode: shippingAddressPostCode = "" } = {},
  } = cart
  let shippingCost = 0
  shippingCost = Number((cart?.shippingTotal?.centAmount ?? 0) / 100).toFixed(2)
  let promoCodes = []
  if (cart?.customLineItems?.length > 0) {
    promoCodes = promoCodes.concat(await getShippingDiscountPromoCodes(cart))
    promoCodes
      .filter(item => item?.type === "shipping")
      .forEach(item => {
        shippingCost = Number(shippingCost) + Number(item.discount)
      })
  }
  if (
    lineItemsLength > 0 &&
    shippingZip &&
    shippingZip.trim() !== "" &&
    shippingAddressPostCode &&
    (!taxedPrice || !shippingCost || (shippingCost && shippingCost * 1 === 0))
  ) {
    await store
      .dispatch(shipmentCart())
      .unwrap()
      .then(response => {
        if (response && response["shipping-details"]) {
          updateProductDetailToStore(response)
        } else {
          getShippingDetails()
            .then(response => {
              updateProductDetailToStore(response)
            })
            .catch(error => {
              console.error(error)
            })
        }
      })
  }
}

const getProfileData = async (errorMsg = "") => {
  let profile = {}
  try {
    const res = await store.dispatch(getProfile())
    if (res && res.data) {
      localStorage.setItem("profile-version", res.data?.version)
      profile = res.data
      profile.postalCode = profile?.addresses[0]?.postalCode
      const profileAddr = profile?.addresses?.find(
        addr => addr.custom?.fields?.label === "Profile"
      )
      profile.profileAddrId = profileAddr?.id
      profile.addresses = profile?.addresses?.filter(
        addr => addr.custom?.fields?.label !== "Profile"
      )
      let addresses = profile?.addresses
      if (profile?.defaultShippingAddressId) {
        const index = addresses.findIndex(
          item => item.id === profile.defaultShippingAddressId
        )
        addresses = arrayMove(profile?.addresses, index, 0)
      }
      profile.addresses = addresses
    }
  } catch (err) {
    errorMsg &&
      store.dispatch(
        showToast({
          message: errorMsg,
          isVisible: true,
        })
      )
  }
  return profile
}

export const removeCartItem = async (data, cb = null) => {
  let payload
  const cart = store?.getState()?.cart?.cart
  const { shippingAddress: { postalCode = "" } = {} } = cart
  if (!Array.isArray(data)) {
    payload = removeFromCart(data.id, data.quantity, data.isComboSku)
  } else {
    payload = setRemoveCartPayload(data)
  }
  const body = {
    actions: payload,
  }

  await store
    .dispatch(removeFromCartLineItem(body))
    .unwrap()
    .then(res => {
      store.dispatch(setCart(res))
      const quantityLineItems = res?.lineItems
      const payload = getQuantityCount(quantityLineItems)
      store.dispatch(setCartCount(payload))
      if (res.lineItems.length > 0) {
        if (postalCode) {
          getShippingMethods()
        }
      } else {
        deleteFromCart(cb)
      }
    })
}
export const deleteFromCart = async cb => {
  await store
    .dispatch(deleteCart())
    .unwrap()
    .then(res => {
      if (res) {
        cb && cb("success", null, res ? res : {})
        store.dispatch(setCart({ lineItems: [] }))
        store.dispatch(setNoshipping([]))
        store.dispatch(setCartCount(0))
        store.dispatch(setShipping([]))
        const cartPayload = { currency: "USD", taxMode: "ExternalAmount" }
        store.dispatch(createCart(cartPayload))
      }
    })
    .catch(() => {
      cb && cb("failure", "failed to empty your cart.")
    })
}
const getProductsById = async ids => {
  let query = ""
  ids.forEach((id, i) => {
    query += `"${id}"`
    if (i + 1 !== ids.length) query += ", "
  })
  try {
    const response = await store
      .dispatch(recentlyViewed(query))
      .unwrap()
      .then(res => {
        if (res?.results) {
          return res.results
        }
      })
    if (response) return response
  } catch (err) {
    console.error("Failed to fetch product details", err)
  }
  return []
}

const getShippingDetails = async () => {
  return new Promise(async (resolve, reject) => {
    const cart = store?.getState()?.cart?.cart
    const shippingDetails = {
      "shipping-details": { results: [] },
      shippingCost: true,
    }
    const response = await apim.get(
      `${shippingCostEndPoint}?cartId=${cart?.id}`
    )
    shippingDetails["shipping-details"]["results"] = response?.data?.results

    if (response) {
      resolve(shippingDetails)
    } else {
      reject(new Error("Cannot ship to your location"))
    }
  })
}

const updateProductDetailToStore = response => {
  let validProducts = []
  let inValidProducts = []
  validProducts = response["shipping-details"]?.results?.filter(
    item => !item.warningMessage
  )
  inValidProducts = response["shipping-details"]?.results?.filter(
    item => item.warningMessage
  )
  store.dispatch(setShipping(validProducts))
  store.dispatch(setNoshipping(inValidProducts))
}
export {
  setShippingAddress,
  getShippingMethods,
  getProfileData,
  getProductsById,
  getShippingDetails,
}
